import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Key,
  Copy,
  Check,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { googleApi } from "@/lib/crmApi";

export default function GoogleConfigPanel({ onChanged }) {
  const [config, setConfig] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const c = await googleApi.getConfig();
      setConfig(c);
      setClientId("");
      setClientSecret("");
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error("Copie impossible");
    }
  };

  const handleSave = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      toast.error("Client ID et Secret requis");
      return;
    }
    setSaving(true);
    try {
      await googleApi.saveConfig({
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
      });
      toast.success("Credentials Google enregistrées");
      setClientId("");
      setClientSecret("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (
      !window.confirm(
        "Supprimer les credentials Google ? Cela déconnectera aussi votre session et supprimera les auto-syncs."
      )
    )
      return;
    try {
      await googleApi.deleteConfig();
      toast.success("Credentials supprimées");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  const redirectUri = config?.redirect_uri || config?.default_redirect_uri || "";
  const origin = redirectUri.replace(/\/api\/.*$/, "");

  return (
    <div className="space-y-5" data-testid="google-config-panel">
      {/* Instructions */}
      <div className="rounded-lg border border-[hsl(var(--accent-navy))]/20 bg-blue-50/40 p-4 text-sm dark:bg-blue-950/20">
        <div className="mb-2 flex items-center gap-2 font-heading font-semibold text-[hsl(var(--accent-navy))]">
          <Info className="h-4 w-4" />
          Comment obtenir vos credentials
        </div>
        <ol className="list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
          <li>
            Allez sur{" "}
            <a
              href="https://console.cloud.google.com/apis/credentials"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[hsl(var(--accent-navy))] hover:underline"
            >
              Google Cloud Console → Credentials
              <ExternalLink className="ml-0.5 inline h-3 w-3" />
            </a>
          </li>
          <li>Créez un projet si nécessaire</li>
          <li>
            Activez <strong>Google Sheets API</strong> et <strong>Google Drive API</strong>
          </li>
          <li>
            Configurez l'<strong>OAuth consent screen</strong> (External) + ajoutez votre email en Test user
          </li>
          <li>
            Créez un <strong>OAuth client ID</strong> type "Web application" et collez ces valeurs :
          </li>
        </ol>

        <Separator className="my-3" />

        <div className="space-y-2">
          <div>
            <div className="text-label mb-1 text-muted-foreground">
              Authorized JavaScript origins
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-2 py-1.5">
              <code className="flex-1 truncate text-xs">{origin}</code>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => copy(origin)}
                data-testid="copy-origin"
              >
                {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>
          <div>
            <div className="text-label mb-1 text-muted-foreground">
              Authorized redirect URI
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-2 py-1.5">
              <code className="flex-1 truncate text-xs">{redirectUri}</code>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => copy(redirectUri)}
                data-testid="copy-redirect"
              >
                {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-3 rounded-lg border border-border/60 bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-muted-foreground" />
            <span className="font-heading text-sm font-semibold">
              Credentials OAuth
            </span>
          </div>
          {config?.configured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
              <Check className="h-3 w-3" />
              Configuré ({config.source === "db" ? "interface" : ".env"})
            </span>
          )}
        </div>

        {config?.configured && config?.client_id_masked && (
          <div className="rounded-md bg-muted/50 px-3 py-2 text-xs">
            <span className="text-muted-foreground">Client ID actuel : </span>
            <code className="font-mono">{config.client_id_masked}</code>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="gconf-cid">
            Client ID
            {config?.configured && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                (laissez vide pour garder l'actuel)
              </span>
            )}
          </Label>
          <Input
            id="gconf-cid"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="xxxxxxxxxx.apps.googleusercontent.com"
            data-testid="gconf-client-id"
            className="font-mono text-xs"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="gconf-secret">Client Secret</Label>
          <div className="relative">
            <Input
              id="gconf-secret"
              type={showSecret ? "text" : "password"}
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="GOCSPX-xxxxxxxxxxxxxxxx"
              data-testid="gconf-client-secret"
              className="pr-10 font-mono text-xs"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1 h-8 w-8"
              onClick={() => setShowSecret((s) => !s)}
            >
              {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button
            onClick={handleSave}
            disabled={saving || !clientId.trim() || !clientSecret.trim()}
            data-testid="gconf-save"
            className="gap-2"
          >
            <Check className="h-4 w-4" />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
          {config?.configured && config?.source === "db" && (
            <Button
              variant="ghost"
              onClick={handleClear}
              data-testid="gconf-clear"
              className="gap-2 text-muted-foreground hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          )}
        </div>

        <p className="pt-1 text-[11px] text-muted-foreground">
          🔒 Les credentials sont stockées chiffrées en base. Seul le Client ID est visible en lecture (masqué), le Secret ne ressort jamais.
        </p>
      </div>
    </div>
  );
}
